import { useCallback, useEffect, useRef } from 'react';
import { isObject } from 'lodash-es';
import { useQueryClient } from '@tanstack/react-query';
import { BLOCK_REFETCH_FREQUENCY_2 } from '../utils/blockchain-consts.ts';
import { debugLog } from '../utils/debug.ts';
import { useBlockchainBlockNumber } from '../contexts/blockchain';
// import { useDeduplicatedBlockNumber } from './useDeduplicatedBlockNumber.ts';
import { useIsBlockchainVersion } from './useIsBlockchainVersion.ts';

export const useBlockchainDataUpdater = () => {
  const blockchainVersion = useIsBlockchainVersion();
  const blockNumber = useBlockchainBlockNumber();

  // Early return in B2B mode - no blockchain updates needed
  if (!blockchainVersion) {
    return { blockNumber: undefined };
  }

  /*const { data: blockNumber } = useDeduplicatedBlockNumber(
    blockchainVersion,
    'globalUpdater'
  );*/

  const lastProcessedBlockRef = useRef<bigint | undefined>(undefined);
  const queryClient = useQueryClient();

  const updateFrequentBlockchainQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;

        if (queryKey[0] !== 'readContract') {
          return false;
        }

        if (!isObject(queryKey[1])) {
          return true;
        }

        if (!('functionName' in queryKey[1])) {
          return true;
        }

        const functionName = queryKey[1].functionName as string;
        return frequentUpdateFunctionNames.includes(functionName);
      },
    });

    debugLog(
      `🔄 [BLOCKCHAIN UPDATER FREQUENT] [${new Date().toISOString()}] Updated frequent blockchain queries at block ${blockNumber}`
    );
  }, [queryClient, blockNumber]);

  const updateInfrequentBlockchainQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey;

        if (queryKey[0] !== 'readContract') {
          return false;
        }

        if (!isObject(queryKey[1])) {
          return true;
        }

        if (!('functionName' in queryKey[1])) {
          return true;
        }

        const functionName = queryKey[1].functionName as string;

        if (avoidedBlockchainFunctionNames.includes(functionName)) {
          return false;
        }

        return !frequentUpdateFunctionNames.includes(functionName);
      },
    });

    debugLog(
      `🔄 [BLOCKCHAIN UPDATER INFREQUENT] [${new Date().toISOString()}] Updated infrequent blockchain queries at block ${blockNumber}`
    );
  }, [queryClient, blockNumber]);

  useEffect(() => {
    if (!blockchainVersion || !blockNumber) return;

    if (blockNumber) {
      debugLog(
        `🎯 [BLOCKCHAIN UPDATER FREQUENT] [${new Date().toISOString()}] Processing frequent update at block ${blockNumber}`
      );
      updateFrequentBlockchainQueries();
    }

    if (blockNumber && blockNumber % BLOCK_REFETCH_FREQUENCY_2 === 0n) {
      lastProcessedBlockRef.current = blockNumber;
      debugLog(
        `🎯 [BLOCKCHAIN UPDATER INFREQUENT] [${new Date().toISOString()}] Processing infrequent update at block ${blockNumber}`
      );
      updateInfrequentBlockchainQueries();
    }
  }, [
    blockchainVersion,
    blockNumber,
    updateFrequentBlockchainQueries,
    updateInfrequentBlockchainQueries,
  ]);

  return { blockNumber };
};

const frequentUpdateFunctionNames = ['currentRound'];

const avoidedBlockchainFunctionNames = ['paused'];
